const SKIP_CLASSES = ['data-table--matrix']
const HIDDEN_LABELS = new Set(['sn', '#', 'sl', 'first login at'])
const SELECT_LABELS = new Set(['all'])
const ACTION_LABELS = new Set(['action', 'actions'])
const CHIP_LABELS = new Set(['status', 'user type', 'type', 'vpn status'])
const KEEP_VISIBLE = new Set(['password'])
const TITLE_LABELS = new Set([
  'subscription',
  'name',
  'server name',
  'app name',
  'rate plan',
  'login',
  'ip',
  'title',
])

function toggleClass(el, name, on) {
  if (on) el.classList.add(name)
  else el.classList.remove(name)
}

function isTableStructureNode(node) {
  if (!(node instanceof Element)) return false
  return (
    node.matches('table, thead, tbody, tr, th') ||
    Boolean(node.querySelector?.('table.data-table, table, thead, tbody, tr'))
  )
}

function shouldRescanTables(mutations) {
  return mutations.some((mutation) => {
    if (mutation.type !== 'childList') return false
    const target = mutation.target
    if (target instanceof Element && target.matches('table, thead, tbody')) return true
    return [...mutation.addedNodes, ...mutation.removedNodes].some(isTableStructureNode)
  })
}

function headerLabel(header) {
  const clone = header.cloneNode(true)
  clone.querySelectorAll('input, button, select, textarea, svg').forEach((node) => node.remove())
  return (clone.textContent || '').replace(/\s+/g, ' ').trim()
}

function cellText(cell) {
  return (cell.innerText || '').replace(/\s+/g, ' ').trim()
}

function isEmptyValue(text) {
  return !text || /^[—–−-]+$/.test(text)
}

function markWrap(table, cards) {
  const wrap = table.closest('.table-wrap')
  if (!wrap) return
  wrap.classList.toggle('table-wrap--cards', cards)
  wrap.classList.toggle('table-wrap--matrix', table.classList.contains('data-table--matrix'))
}

function enhanceTable(table) {
  try {
    enhanceTableUnsafe(table)
  } catch {
    table.classList.remove('data-table--cards')
    markWrap(table, false)
  }
}

function enhanceTableUnsafe(table) {
  if (SKIP_CLASSES.some((name) => table.classList.contains(name)) || table.closest('.form-table-wrap')) {
    table.classList.remove('data-table--cards')
    markWrap(table, false)
    return
  }

  const headers = Array.from(table.querySelectorAll('thead th')).map(headerLabel)
  if (!headers.length) {
    markWrap(table, false)
    return
  }

  table.querySelectorAll('tbody tr').forEach((row) => {
    const cells = Array.from(row.children)
    if (cells.length === 1 && cells[0].hasAttribute('colspan')) {
      cells[0].removeAttribute('data-label')
      cells[0].classList.remove('is-card-title', 'is-card-select', 'is-card-empty', 'is-card-chip')
      return
    }

    let titleCell = null

    cells.forEach((cell, index) => {
      const label = headers[index] || ''
      const key = label.toLowerCase()
      if (cell.getAttribute('data-label') !== label) cell.setAttribute('data-label', label)

      if (SELECT_LABELS.has(key)) {
        toggleClass(cell, 'is-card-select', true)
        toggleClass(cell, 'is-card-empty', false)
        toggleClass(cell, 'is-card-chip', false)
        return
      }

      if (HIDDEN_LABELS.has(key)) {
        toggleClass(cell, 'is-card-select', false)
        toggleClass(cell, 'is-card-empty', true)
        toggleClass(cell, 'is-card-chip', false)
        return
      }

      if (ACTION_LABELS.has(key) || KEEP_VISIBLE.has(key)) {
        toggleClass(cell, 'is-card-select', false)
        toggleClass(cell, 'is-card-empty', false)
        toggleClass(cell, 'is-card-chip', false)
        return
      }

      const text = cellText(cell)
      const hasControl = Boolean(
        cell.querySelector('button, a, input, select, textarea, [role="status"]')
      )
      const hideEmpty =
        isEmptyValue(text) && (!hasControl || !cell.querySelector('input[type="checkbox"]'))

      toggleClass(cell, 'is-card-select', false)
      toggleClass(cell, 'is-card-empty', hideEmpty)
      toggleClass(cell, 'is-card-chip', !hideEmpty && CHIP_LABELS.has(key))

      if (!hideEmpty && !titleCell && (TITLE_LABELS.has(key) || index === 1)) {
        titleCell = cell
      }
    })

    cells.forEach((cell) => toggleClass(cell, 'is-card-title', cell === titleCell))
  })

  table.classList.add('data-table--cards')
  markWrap(table, true)
}

export function initResponsiveTables(root = document) {
  let frame = 0

  const apply = () => {
    try {
      root.querySelectorAll('table.data-table').forEach(enhanceTable)
    } catch {
      /* keep list pages usable if a table cannot be labeled */
    }
  }

  const schedule = () => {
    if (frame) return
    frame = window.requestAnimationFrame(() => {
      frame = 0
      apply()
    })
  }

  apply()

  const observer = new MutationObserver((mutations) => {
    if (shouldRescanTables(mutations)) schedule()
  })
  observer.observe(root.documentElement || root, { childList: true, subtree: true })

  return () => {
    observer.disconnect()
    if (frame) window.cancelAnimationFrame(frame)
  }
}
